import React, { useCallback, useId, useState } from 'react';
import styled from 'styled-components';
import { Wallet } from 'ethers';
import { FiCheck, FiCopy, FiKey } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { copyTextToClipboard } from '../../blockchain/offlineSigning';

export const WalletInputRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.5rem;

  input {
    flex: 1;
    min-width: 0;
  }
`;

export const GenerateWalletBtn = styled.button.attrs({ type: 'button' })`
  flex-shrink: 0;
  padding: 0 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.45);
  background: rgba(79, 172, 254, 0.12);
  color: #7dd3fc;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(79, 172, 254, 0.22);
    border-color: #4facfe;
    color: #fff;
  }
`;

const KeyRevealBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const KeyRevealCard = styled.div`
  background: linear-gradient(135deg, #1e1b3d 0%, #151028 100%);
  border: 1px solid rgba(248, 113, 113, 0.35);
  border-radius: 14px;
  max-width: 520px;
  width: 100%;
  padding: 1.35rem 1.5rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
`;

const KeyRevealTitle = styled.h3`
  margin: 0 0 0.5rem;
  color: #fecaca;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const KeyRevealWarning = styled.p`
  margin: 0 0 1rem;
  color: #fca5a5;
  font-size: 0.88rem;
  line-height: 1.55;
  font-weight: 600;
`;

const SecretBox = styled.div`
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.75rem;
  font-family: Consolas, Monaco, monospace;
  font-size: 0.78rem;
  word-break: break-all;
  color: #e2e8f0;
`;

const SecretLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 0.35rem;
  font-weight: 600;
`;

const SecretRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  button {
    flex-shrink: 0;
    padding: 0.35rem 0.5rem;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.06);
    color: #e2e8f0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.72rem;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
    }
  }
`;

const AckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 1rem 0;
  cursor: pointer;
  color: #cbd5e1;
  font-size: 0.85rem;
  line-height: 1.45;

  input {
    margin-top: 0.2rem;
    flex-shrink: 0;
    accent-color: #4facfe;
  }
`;

const KeyRevealActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const KeyRevealButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &.primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: #fff;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

/**
 * Browser-only wallet generation with a one-time private key display.
 * @param {object} opts
 * @param {string} opts.walletAddress current draft address
 * @param {(address: string) => void} opts.setWalletAddress
 */
export function useOneTimeWalletReveal({ walletAddress, setWalletAddress }) {
  const [reveal, setReveal] = useState(null);
  const [revealAcknowledged, setRevealAcknowledged] = useState(false);

  const walletFieldEmpty = !String(walletAddress || '').trim();

  const generateWallet = useCallback(() => {
    if (!walletFieldEmpty) {
      toast.error('Clear the wallet field first to generate a new account.');
      return;
    }
    const w = Wallet.createRandom();
    setWalletAddress(w.address);
    setRevealAcknowledged(false);
    setReveal({ address: w.address, privateKey: w.privateKey });
  }, [walletFieldEmpty, setWalletAddress]);

  const dismissKeyReveal = useCallback(() => {
    setReveal(null);
    setRevealAcknowledged(false);
  }, []);

  const cancelKeyGeneration = useCallback(() => {
    setReveal(null);
    setRevealAcknowledged(false);
    setWalletAddress('');
    toast(
      'Generation cancelled. Wallet field cleared — generate again or paste an address if you still need one.'
    );
  }, [setWalletAddress]);

  return {
    reveal,
    revealAcknowledged,
    setRevealAcknowledged,
    generateWallet,
    dismissKeyReveal,
    cancelKeyGeneration,
    walletFieldEmpty,
    keyRevealOpen: Boolean(reveal)
  };
}

/**
 * @param {object} props
 * @param {{ address: string, privateKey: string } | null} props.reveal
 * @param {boolean} props.revealAcknowledged
 * @param {(v: boolean) => void} props.onRevealAcknowledgedChange
 * @param {() => void} props.onConfirmBackup
 * @param {() => void} props.onCancelDiscard
 * @param {string} [props.publicAddressCaption]
 */
export function OneTimeKeyRevealDialog({
  reveal,
  revealAcknowledged,
  onRevealAcknowledgedChange,
  onConfirmBackup,
  onCancelDiscard,
  publicAddressCaption = 'Public address (also filled in the form)'
}) {
  const titleId = useId();

  if (!reveal) return null;

  return (
    <KeyRevealBackdrop
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={() =>
        toast.error('Use the buttons below. Confirm you saved the private key before continuing.')
      }
    >
      <KeyRevealCard onClick={(e) => e.stopPropagation()}>
        <KeyRevealTitle id={titleId}>
          <FiKey aria-hidden />
          Save your credentials now
        </KeyRevealTitle>
        <KeyRevealWarning>
          The <strong>private key</strong> below is extremely confidential. It is shown <strong>only this once</strong> in
          this browser session. We do not send it to the server and we do not store it. Anyone with this key can control
          funds at this address. Copy it to a password manager or offline backup, then confirm below.
        </KeyRevealWarning>
        <SecretLabel>{publicAddressCaption}</SecretLabel>
        <SecretRow>
          <SecretBox style={{ marginBottom: 0, flex: 1 }}>{reveal.address}</SecretBox>
          <button
            type="button"
            onClick={async () => {
              await copyTextToClipboard(reveal.address);
              toast.success('Address copied');
            }}
          >
            <FiCopy /> Copy
          </button>
        </SecretRow>
        <SecretLabel style={{ marginTop: '0.85rem' }}>Private key (never share or commit)</SecretLabel>
        <SecretRow>
          <SecretBox style={{ marginBottom: 0, flex: 1 }}>{reveal.privateKey}</SecretBox>
          <button
            type="button"
            onClick={async () => {
              await copyTextToClipboard(reveal.privateKey);
              toast.success('Private key copied');
            }}
          >
            <FiCopy /> Copy
          </button>
        </SecretRow>
        <AckRow>
          <input
            type="checkbox"
            checked={revealAcknowledged}
            onChange={(e) => onRevealAcknowledgedChange(e.target.checked)}
          />
          <span>I have stored the private key in a secure place. I understand it will not be shown again.</span>
        </AckRow>
        <KeyRevealActions>
          <KeyRevealButton type="button" className="secondary" onClick={onCancelDiscard}>
            Cancel — discard this wallet
          </KeyRevealButton>
          <KeyRevealButton
            type="button"
            className="primary"
            disabled={!revealAcknowledged}
            onClick={onConfirmBackup}
          >
            <FiCheck />
            Continue — I have backed up the private key
          </KeyRevealButton>
        </KeyRevealActions>
      </KeyRevealCard>
    </KeyRevealBackdrop>
  );
}
